import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import { getHostEntityStatusCode, getStatusChipMeta } from '../helpers/utils';

export function ActiveTerminalsPanel({
  terminals,
  terminalsLoading,
  translations,
  onOpenTerminalDetails,
}) {
  return (
    <Card
      variant='outlined'
      sx={{
        borderRadius: 3,
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        border: `1px solid rgba(5, 5, 51, 0.1)`,
        boxShadow:
          '0 2px 8px rgba(5, 5, 51, 0.08), 0 6px 20px rgba(5, 5, 51, 0.07)',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardHeader
        avatar={<DevicesOtherIcon fontSize='small' />}
        title={`${translations.activeTerminals || 'Active Terminals'} (${terminals.length})`}
        sx={{
          background:
            'linear-gradient(90deg, #050A30 0%, #0B1F4D 55%, #143A7B 100%)',
          '& .MuiCardHeader-avatar': {
            color: '#FFFFFF',
            mr: 1,
          },
          '& .MuiCardHeader-title': {
            fontFamily: 'BrightstarSans-Bold',
            fontSize: 16,
            color: '#FFFFFF',
          },
          '& .MuiCardHeader-subheader': {
            color: 'rgba(255, 255, 255, 0.85)',
          },
          '& .MuiCardHeader-action': {
            color: '#FFFFFF',
            alignSelf: 'center',
            '& .MuiLink-root': {
              color: 'rgba(255, 255, 255, 0.92)',
              fontWeight: 500,
              '&:hover': { color: '#FFFFFF' },
            },
          },
        }}
      />
      <Divider />
      <CardContent sx={{ pt: 1.5 }}>
        {terminalsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant='body2' color='text.secondary'>
              Loading…
            </Typography>
          </Box>
        ) : terminals.length ? (
          <List dense disablePadding>
            {terminals.map(terminal => {
              const terminalStatusMeta = getStatusChipMeta(
                getHostEntityStatusCode(terminal),
                translations,
              );
              const esConnectId = String(
                terminal.esConnectTerminalId || '',
              ).trim();

              return (
                <ListItem
                  key={terminal.terminalOid ?? terminal.terminalId}
                  disableGutters
                  sx={{ mb: 0.75, p: 0 }}
                >
                  <Box
                    component='button'
                    onClick={() => onOpenTerminalDetails(terminal)}
                    type='button'
                    sx={{
                      width: '100%',
                      py: 1,
                      px: 1.25,
                      borderRadius: 2,
                      border: '1px solid rgba(5, 5, 51, 0.08)',
                      textAlign: 'left',
                      background: 'transparent',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(5, 5, 51, 0.1)',
                        borderColor: 'var(--reach-blue)',
                        transform: 'translateY(-1px)',
                        '& .terminal-card-chevron': {
                          opacity: 0.75,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ minWidth: 0, pr: 1 }}>
                        <Typography
                          component='span'
                          noWrap
                          sx={{
                            fontFamily: 'BrightstarSans-Bold',
                            fontSize: '1rem',
                            fontWeight: 700,
                            color: 'var(--reach-blue)',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {`${terminal.description} • ${terminal.terminalId}`}
                        </Typography>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          noWrap
                          sx={{ mt: 0.35, display: 'block', lineHeight: 1.35 }}
                        >
                          {`esConnect: ${esConnectId || '—'}`}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          ml: 1,
                        }}
                      >
                        {terminalStatusMeta.label && (
                          <Chip
                            size='small'
                            label={terminalStatusMeta.label}
                            variant={terminalStatusMeta.chipVariant}
                            sx={{ ...terminalStatusMeta.chipSx, flexShrink: 0 }}
                          />
                        )}
                        <ChevronRightIcon
                          className='terminal-card-chevron'
                          sx={{
                            fontSize: 20,
                            color: 'text.secondary',
                            opacity: 0.4,
                            flexShrink: 0,
                            transition: 'opacity 0.2s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ py: 4, textAlign: 'center' }}
          >
            No active terminals available for this retailer.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function RecentOrdersPanel({
  recentConsumables = [],
  recentConsumablesLoading = false,
  translations,
  onViewAllConsumables,
}) {
  return (
    <Card
      variant='outlined'
      sx={{
        borderRadius: 3,
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        border: `1px solid rgba(5, 5, 51, 0.1)`,
        boxShadow:
          '0 2px 8px rgba(5, 5, 51, 0.08), 0 6px 20px rgba(5, 5, 51, 0.07)',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardHeader
        avatar={<ReceiptLongIcon fontSize='small' />}
        title={`${translations.recentOrders || 'Recent Consumable Orders'}`}
        action={
          <Link
            component='button'
            type='button'
            onClick={onViewAllConsumables}
            underline='hover'
            sx={{ fontSize: 14, pr: 1.5 }}
          >
            {translations.viewAll || 'View all'}
          </Link>
        }
        sx={{
          background:
            'linear-gradient(90deg, #050A30 0%, #0B1F4D 55%, #143A7B 100%)',
          '& .MuiCardHeader-avatar': {
            color: '#FFFFFF',
            mr: 1,
          },
          '& .MuiCardHeader-title': {
            fontFamily: 'BrightstarSans-Bold',
            fontSize: 16,
            color: '#FFFFFF',
          },
          '& .MuiCardHeader-subheader': {
            color: 'rgba(255, 255, 255, 0.85)',
          },
          '& .MuiCardHeader-action': {
            color: '#FFFFFF',
            alignSelf: 'center',
            '& .MuiLink-root': {
              color: 'rgba(255, 255, 255, 0.92)',
              fontWeight: 500,
              '&:hover': { color: '#FFFFFF' },
            },
          },
        }}
      />
      <Divider />
      <CardContent sx={{ pt: 1.5 }}>
        {recentConsumablesLoading ? (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ py: 4, textAlign: 'center' }}
          >
            Loading…
          </Typography>
        ) : recentConsumables.length ? (
          <List dense disablePadding>
            {recentConsumables.slice(0, 3).map(item => (
              <ListItem
                key={item.id}
                disableGutters
                sx={{
                  py: 0.75,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <ListItemText
                  primary={item.formName}
                  secondary={`${item.submissionTime} • ${item.origin}`}
                  primaryTypographyProps={{
                    fontFamily: 'BrightstarSans-Bold',
                    fontSize: 14,
                    color: 'var(--reach-blue)',
                  }}
                  secondaryTypographyProps={{ fontSize: 13 }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ py: 4, textAlign: 'center' }}
          >
            No recent consumable orders available for this retailer.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function RecentFormsPanel({
  recentForms,
  recentFormsLoading,
  translations,
  onViewAllForms,
}) {
  return (
    <Card
      variant='outlined'
      sx={{
        borderRadius: 3,
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        border: `1px solid rgba(5, 5, 51, 0.1)`,
        boxShadow:
          '0 2px 8px rgba(5, 5, 51, 0.08), 0 6px 20px rgba(5, 5, 51, 0.07)',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardHeader
        avatar={<DescriptionIcon fontSize='small' />}
        title={`${translations.recentForms || 'Recent Forms'}`}
        action={
          <Link
            component='button'
            type='button'
            onClick={onViewAllForms}
            underline='hover'
            sx={{ fontSize: 14, pr: 1.5 }}
          >
            {translations.viewAll || 'View all'}
          </Link>
        }
        sx={{
          background:
            'linear-gradient(90deg, #050A30 0%, #0B1F4D 55%, #143A7B 100%)',
          '& .MuiCardHeader-avatar': {
            color: '#FFFFFF',
            mr: 1,
          },
          '& .MuiCardHeader-title': {
            fontFamily: 'BrightstarSans-Bold',
            fontSize: 16,
            color: '#FFFFFF',
          },
          '& .MuiCardHeader-subheader': {
            color: 'rgba(255, 255, 255, 0.85)',
          },
          '& .MuiCardHeader-action': {
            color: '#FFFFFF',
            alignSelf: 'center',
            '& .MuiLink-root': {
              color: 'rgba(255, 255, 255, 0.92)',
              fontWeight: 500,
              '&:hover': { color: '#FFFFFF' },
            },
          },
        }}
      />
      <Divider />
      <CardContent sx={{ pt: 1.5 }}>
        {recentFormsLoading ? (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ py: 4, textAlign: 'center' }}
          >
            Loading…
          </Typography>
        ) : recentForms.length ? (
          <List dense disablePadding>
            {recentForms.slice(0, 3).map(form => (
              <ListItem
                key={form.id}
                disableGutters
                sx={{
                  py: 0.75,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <ListItemText
                  primary={form.formName}
                  secondary={`${form.submissionTime} • ${form.submitter}`}
                  primaryTypographyProps={{
                    fontFamily: 'BrightstarSans-Bold',
                    fontSize: 14,
                    color: 'var(--reach-blue)',
                  }}
                  secondaryTypographyProps={{ fontSize: 13 }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ py: 4, textAlign: 'center' }}
          >
            No recent forms available for this retailer.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
